export type SendButtonDisabledReasons = {
  hasShortenedUrl?: boolean;
  isOverCharLimit?: boolean;
  isFormIncomplete?: boolean;
};

export type DisabledReasonInfo = {
  message: string;
  descriptionId: string;
} | null;

/**
 * Disable reason type constants
 */
export const DISABLE_REASON_TYPES = {
  SHORTENED_URL: 'shortened-url',
  CHAR_LIMIT: 'char-limit',
  INCOMPLETE: 'incomplete',
  NONE: 'none',
} as const;

type DisableReasonType = (typeof DISABLE_REASON_TYPES)[keyof typeof DISABLE_REASON_TYPES];

/**
 * Disable reason configuration: messages and description IDs
 * All disable reason metadata is defined here
 */
type DisableReasonConfigType = Record<
  Exclude<DisableReasonType, typeof DISABLE_REASON_TYPES.NONE>,
  { messageKey: string; descriptionId: string }
>;

export const DISABLE_REASON_CONFIG: DisableReasonConfigType = {
  [DISABLE_REASON_TYPES.SHORTENED_URL]: {
    messageKey:
      'This link cannot be sent because it appears to be a shortened URL. Mobile carriers block shortened links as they look like spam. Please paste the full web address instead.',
    descriptionId: 'send-button-disabled-shortened-url',
  },
  [DISABLE_REASON_TYPES.CHAR_LIMIT]: {
    messageKey: 'Character limit exceeded. Please reduce the message length and try again.',
    descriptionId: 'send-button-disabled-char-limit',
  },
  [DISABLE_REASON_TYPES.INCOMPLETE]: {
    messageKey: 'Please complete all required fields before sending.',
    descriptionId: 'send-button-disabled-incomplete',
  },
};

function getPrimaryDisableReasonType(reasons: SendButtonDisabledReasons): DisableReasonType {
  const { hasShortenedUrl, isOverCharLimit, isFormIncomplete } = reasons;

  if (hasShortenedUrl) return DISABLE_REASON_TYPES.SHORTENED_URL;
  if (isOverCharLimit) return DISABLE_REASON_TYPES.CHAR_LIMIT;
  if (isFormIncomplete) return DISABLE_REASON_TYPES.INCOMPLETE;
  return DISABLE_REASON_TYPES.NONE;
}

export function getSendButtonDisabledReason(
  reasons: SendButtonDisabledReasons,
  t: (key: string, options?: Record<string, any>) => string
): DisabledReasonInfo {
  const reasonType = getPrimaryDisableReasonType(reasons);

  if (reasonType === DISABLE_REASON_TYPES.NONE) {
    return null;
  }

  const config = DISABLE_REASON_CONFIG[reasonType];
  return {
    message: t(config.messageKey),
    descriptionId: config.descriptionId,
  };
}
